import { useMatch, useSearch } from '@tanstack/react-router';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
} from '.';
import { useTranslation } from 'react-i18next';
import { RowsPerPageSelect } from './rowsPerPageSelect';
import { useAppContext } from '@hooks/useAppContext';
import { useMemo, useState } from 'react';
import { getCacheKeyFromRoute } from '@utils/apiHelpers';
import { DEFAULT_ROWS_PER_PAGE } from '@utils/consts';
import { MetaData } from '@root/globalTypes';

type TablePaginationProps = {
  meta: MetaData;
  items: number;
};

export const TablePagination = ({ meta, items }: TablePaginationProps) => {
  const { t } = useTranslation('app', { keyPrefix: 'pagination' });
  const search = useSearch({ from: '__root__' });
  const match = useMatch({ strict: false });

  const { rowsPerPageCache } = useAppContext();
  const key = useMemo(() => getCacheKeyFromRoute(match.routeId), [match]);
  const [rowsPerPage, setRowsPerPage] = useState(
    search.items || rowsPerPageCache?.[key] || DEFAULT_ROWS_PER_PAGE,
  );

  const page = meta.currentPage || Number(search.page) || 1;
  const hasPrev = meta.prevPage !== null;
  const hasNext = meta.nextPage !== null;

  if (!items) return null;

  return (
    <div className="-mt-8 mb-4 flex w-full flex-col items-center justify-between gap-2 rounded-b bg-white px-5 py-6 md:flex-row md:gap-0">
      <div className="mx-auto flex flex-col items-center gap-2 md:flex-row md:gap-4">
        <Pagination className="w-auto">
          <PaginationContent>
            {hasPrev && (
              <PaginationItem>
                <PaginationPrevious
                  to="./"
                  search={{
                    ...search,
                    page: page - 1,
                  }}
                />
              </PaginationItem>
            )}
            <PaginationItem>
              <span className="flex h-10 w-10 items-center justify-center text-black">{page}</span>
            </PaginationItem>
            {hasNext && (
              <PaginationItem>
                <PaginationNext
                  to="./"
                  search={{
                    ...search,
                    page: page + 1,
                  }}
                />
              </PaginationItem>
            )}
          </PaginationContent>
        </Pagination>
      </div>

      <div className="flex items-center gap-2 text-black">
        <span>{t('rowsPerPage')}</span>
        <RowsPerPageSelect rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage} />
      </div>
    </div>
  );
};
